import React from 'react';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { StoryObject } from '@benzinga/advanced-news-manager';
import { News } from '@benzinga/basic-news-manager';
import { getSponsoredContentLabel } from '@benzinga/article-manager/sponsored-utils';
import { isPaywallArticle, isProOnlyPost } from '@benzinga/article-manager';
import { Ticker } from '@benzinga/ticker-ui';
import { PostDateType, PostElapsed } from '../PostCard/PostElapsed';
import { BzImage } from '@benzinga/image';
import { formatImageUrl, convertToRelativeUrl } from '@benzinga/utils';
import { NewsElementCommentCount } from '../NewsElementCommentCount';
import { NoFirstRender } from '@benzinga/hooks';
import { InView } from 'react-intersection-observer';
import { isGlobalImpressionStored, storeGlobalImpression, Term } from '@benzinga/content-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';
import type { LocaleType } from '@benzinga/translate';
import { ProBadge, EdgeBadge } from '@benzinga/core-ui';
import classNames from 'classnames';

export interface NewsListItemElementProps {
  contentId?: string;
  title?: string;
  variant?: 'primary' | 'secondary';
  node: StoryObject | News;
  primaryTerm?: Term | undefined;
  showAuthor?: boolean;
  showImage?: boolean;
  showTeaser?: boolean;
  showTicker?: boolean;
  dateType?: PostDateType;
  showTime?: boolean;
  locale?: LocaleType;
  showCommentsIcon?: boolean;
  inLineTicker?: boolean;
  maxVisibleTickers?: number;
}

export const NewsListItemElement: React.FC<NewsListItemElementProps> = ({
  contentId,
  dateType = 'relative',
  inLineTicker = false,
  locale = 'en',
  maxVisibleTickers,
  node,
  primaryTerm = undefined,
  showAuthor = false,
  showCommentsIcon = false,
  showImage = false,
  showTeaser = false,
  showTicker = true,
  showTime = true,
  variant = 'primary',
}) => {
  const dataAction = contentId ? `${contentId} Article Click` : 'Article Click';
  const tickers = Array.isArray(node?.stocks) ? node.stocks.map(ticker => ticker.name) : [];
  const sponsoredData = getSponsoredContentLabel(node);
  const href = node.url ? node.url : `https://www.benzinga.com/article/${node.id}`;
  const date = node.created || node.updated;
  const image = formatImageUrl(node);
  const imageSize = inLineTicker && showTeaser ? 126 : 90;

  const imageAlt = (node as StoryObject)?.assets?.[0]?.title ?? '';
  const session = React.useContext(SessionContext);

  const registerImpression = isVisible => {
    if (isVisible && !isGlobalImpressionStored(`sponsored-item-${node.id}`)) {
      storeGlobalImpression(`sponsored-item-${node.id}`);
      session.getManager(TrackingManager).trackCampaignEvent('view', {
        partner_id: (node?.meta?.Reach?.Disclosure?.tid || 0).toString(),
        unit_type: `sponsored-item-${node.id}`,
      });
    }
  };

  const imageWrapperStyle = {
    height: `${imageSize}px`,
    minWidth: `${imageSize}px`,
    width: `${imageSize}px`,
  };

  const isProPost = isProOnlyPost(node as StoryObject);
  const hasBadges = isProPost || isPaywallArticle(node);
  const hasMetaFooter = showAuthor || showTicker || primaryTerm || showCommentsIcon;
  const isCompactListItem = !showImage && !showTicker;
  const primaryHref = isProPost ? '#' : convertToRelativeUrl(href);
  const secondaryHref = isProPost ? '#' : locale === 'en' ? convertToRelativeUrl(href) : href;

  return (
    <div className={`leading-normal ${variant}`}>
      {variant === 'primary' ? (
        <div className="&_.title-text:hover]:text-blue-600 [&_.teaser-text]:text-gray-900 [&_.teaser-text:hover]:text-blue-600">
          <div className="flex w-full">
            {image && showImage && (
              <div className="mr-4 rounded overflow-hidden" style={imageWrapperStyle}>
                <BzImage
                  alt={imageAlt}
                  blurDataURL={`${image}?width=10`}
                  height={imageSize}
                  layout="fixed"
                  loaderProps={{ width: 300 }}
                  loading="lazy"
                  objectFit="cover"
                  src={image}
                  width={imageSize}
                />
              </div>
            )}
            <div
              className={classNames('flex flex-col w-full', {
                'gap-1': !showImage && !isCompactListItem,
                'gap-1.5': isCompactListItem,
                'justify-between': showImage,
                'pl-2': showImage && !image,
              })}
            >
              {sponsoredData.isSponsored ? (
                <InView onChange={isVisible => registerImpression(isVisible)}>
                  <span className="block text-[13px] text-blue-500 font-semibold">{sponsoredData.label}</span>
                </InView>
              ) : null}
              <a
                className={classNames('text-[inherit]', { 'cursor-default': isProPost })}
                data-action={dataAction}
                href={primaryHref}
              >
                {node.title && (
                  <h3 className="title-text text-base font-semibold line-clamp-3 md:line-clamp-2 text-gray-900 hover:text-blue-600">
                    {hasBadges && (
                      <span className="inline-block align-middle mr-2 flex-shrink-0 scale-90">
                        {isProPost ? <ProBadge /> : EdgeBadge}
                      </span>
                    )}
                    <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(node.title) }} />
                  </h3>
                )}
                {showTeaser && node.teaser && (
                  <div className="teaser-text text-base font-normal line-clamp-2 md:line-clamp-2 max-md:line-clamp-4 max-md:font-normal">
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(node.teaser) }} />
                  </div>
                )}
              </a>
              {isCompactListItem && !sponsoredData.isSponsored && date && !hasMetaFooter ? (
                <PostElapsed
                  className="article-datetime text-bzgray-600 whitespace-nowrap text-xs"
                  created={date}
                  dateType={dateType}
                  locale={locale}
                  showTime={showTime}
                />
              ) : (
                <div
                  className={classNames({
                    'flex flex-col md:flex-row-reverse items-start md:items-center justify-between py-1 pr-1 w-full':
                      inLineTicker && showTicker,
                    'flex items-center gap-2': !inLineTicker || !showTicker,
                  })}
                >
                  {showAuthor && !sponsoredData.isSponsored && node.author ? (
                    <span className="article-author text-sm font-semibold mr-2">{node.author}</span>
                  ) : null}
                  <div className={classNames('flex items-center', { 'mt-auto': showImage && hasMetaFooter })}>
                    {!sponsoredData.isSponsored && date ? (
                      <PostElapsed
                        className="article-datetime text-bzgray-600 whitespace-nowrap"
                        created={date}
                        dateType={dateType}
                        locale={locale}
                        showTime={showTime}
                      />
                    ) : null}
                    {showCommentsIcon && !sponsoredData.isSponsored && (
                      <NoFirstRender fallback={<div className="h-[25px]" />}>
                        <div className="flex items-center ml-2 text-sm text-gray-500">
                          <span className="mr-2 font-light">|</span>
                          <NewsElementCommentCount id={node.id} url={href} />
                        </div>
                      </NoFirstRender>
                    )}
                  </div>
                  <div className="flex items-center">
                    {primaryTerm ? (
                      <a
                        className="inline-block text-[rgb(29,69,107)] border border-gray-400 rounded-[0.2rem] px-2 mt-1 text-xs font-medium leading-6 h-[24.5px] text-center capitalize hover:bg-blue-100 mr-2"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(primaryTerm.name) }}
                        href={
                          primaryTerm.url_public ? `/${primaryTerm.url_public}` : `/taxonomy/term/${primaryTerm.tid}`
                        }
                      />
                    ) : null}
                    <React.Suspense>
                      {showTicker && tickers && tickers?.length ? (
                        <TickerList maxVisibleTickers={maxVisibleTickers} tickers={tickers} />
                      ) : null}
                    </React.Suspense>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex">
          {date && (
            <div className="whitespace-nowrap pr-2 min-w-[130px]">
              <PostElapsed
                className="article-datetime"
                created={date}
                dateType={dateType}
                locale={locale}
                showTime={showTime}
              />
            </div>
          )}
          <div>
            <ul className="flex flex-wrap gap-1">
              {showTicker &&
                tickers &&
                tickers.map((ticker, index) => {
                  return (
                    <li key={index}>
                      <Ticker key={index} symbol={ticker} />
                    </li>
                  );
                })}
            </ul>
            <span>
              {node?.title && (
                <a
                  className={classNames('title-text text-black hover:text-blue-600', {
                    'cursor-default': isProPost,
                  })}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(node.title) }}
                  data-action={dataAction}
                  href={secondaryHref}
                />
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const TickerList = ({ maxVisibleTickers, tickers }: { tickers: string[]; maxVisibleTickers?: number }) => {
  let visibleTickers: string[];
  let hiddenTickers: string[];

  if (maxVisibleTickers !== undefined) {
    visibleTickers = tickers.slice(0, maxVisibleTickers);
    hiddenTickers = tickers.slice(maxVisibleTickers);
  } else {
    const maxItems = 4;
    const showOthers = tickers.length > maxItems;
    const visibleCount = showOthers ? maxItems - 1 : tickers.length;
    visibleTickers = tickers.slice(0, visibleCount);
    hiddenTickers = tickers.slice(visibleCount);
  }

  return (
    <div className="ticker-list">
      <div className="flex flex-wrap gap-2 mt-1">
        {visibleTickers.map((ticker: string, tickerIndex: number) => {
          return <Ticker key={tickerIndex} symbol={ticker} />;
        })}
        {hiddenTickers.length > 0 ? (
          <div className="relative group [&_.ticker-box]:border-none">
            <div className="inline-flex items-baseline bg-white rounded border border-gray-300 text-gray-600 font-bold px-1 text-sm">
              {hiddenTickers.length} others
            </div>
            <div className="absolute hidden group-hover:block max-h-[120px] my-0.5 overflow-y-scroll z-50 items-baseline bg-white rounded border border-gray-300 text-gray-600 font-bold px-1 text-sm">
              {hiddenTickers.map((ticker: string, tickerIndex: number) => {
                return (
                  <div key={tickerIndex}>
                    <Ticker hidePopup={true} symbol={ticker} />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
